import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  IconLoader,
  PermissionDenied,
  PermissionEnum,
  formatDate,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
  IconEdit,
  useFilters,
  Table,
  IconExpand,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  ProductRiskListItem,
  RiskScoreCalculationType,
  RiskScoreClientTypeEnum,
  RiskScoreItem,
  RiskScoreType,
} from '../../types';
import {
  productRiskListColumns,
  productRiskListSchema,
  productRiskListFiltersSchema,
} from '../variables/products';
import {
  useLazyGetFactorsQuery,
  useUpdateFactorMutation,
} from '../../../store/dictionaries/services';
import CalculateForm from '../../../components/products/calculate-form';
import { ProductsListTypes } from './types';

import ROUTES from '../../../routes';

import cn from '../partial.module.css';

const formatProductRiskList = (
  list: ProductRiskListItem[],
): ProductRiskListItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      updatedAt: (
        <div className={cn.dateUpdated}>
          {formatDate(item.updatedAt, true)}
          <span>
            Edit
            <IconEdit width={16} height={16} />
          </span>
        </div>
      ),
      riskCategory: (
        <div>{`${item.riskCategory.charAt(0)}${item.riskCategory
          .slice(1)
          .toLowerCase()}`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const ClientsProductsList = ({
  list,
  pagination,
  getList,
  isFetching,
  clientType,
  isLoading,
  errorsList,
}: ProductsListTypes): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { pathname } = useLocation();

  const {
    amlDictionaries: { activePermissions, factors },
  } = useAppSelector((state) => state);

  const { control, watch, reset, handleSubmit } = useForm<{
    calculationType: RiskScoreCalculationType | undefined;
  }>({ defaultValues: { calculationType: undefined } });

  const { sort, onFilterChange, filters } = useQueryState(
    productRiskListSchema,
  );

  const formattedData = useMemo(() => {
    const data = formatProductRiskList(list as ProductRiskListItem[]);

    return data;
  }, [list, sort]);

  const calculate = watch('calculationType');

  const [getFactors] = useLazyGetFactorsQuery();

  const prevFilters = usePrevious(filters);

  const currentFactor = factors?.find((item) =>
    clientType === RiskScoreClientTypeEnum.INDIVIDUAL
      ? item.type === RiskScoreType.PERSONAL_PRODUCT
      : item.type === RiskScoreType.CORPORATE_PRODUCT,
  );

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<RiskScoreItem>({
      getList,
      onFilterChange,
      onSortChange: () => {},
      schema: productRiskListSchema,
    });

  const [updateFactor, { isLoading: submitInProgress }] =
    useUpdateFactorMutation();

  const onCalculateSubmit = handleSubmit((formValues) => {
    if (currentFactor) {
      updateFactor({
        id: currentFactor?.id,
        body: {
          ...currentFactor,
          calculationType:
            formValues.calculationType as RiskScoreCalculationType,
        },
      });
    }
  });

  useScroll({ element: scrollRef, requestInProgress: isFetching as boolean });

  const { filtersComponent, handleSubmit: productsListSubmit } = useFilters({
    filtersSchema: productRiskListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useEffect(() => {
    if (currentFactor) {
      reset({ calculationType: currentFactor.calculationType });
    }
  }, [currentFactor, reset, pathname, pathname]);

  useEffect(() => {
    if (prevFilters && !Object.keys(prevFilters).length && filters && !list) {
      getListAction({ filters });
    }
  }, [filters, prevFilters, list]);

  useEffect(() => {
    if (!factors) {
      getFactors();
    }
  }, [factors]);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.productRiskLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    activePermissions?.includes(PermissionEnum.SETTINGS_PRODUCTS_LIST)
  ) {
    content = (
      <Table
        columns={productRiskListColumns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={{}}
        onFilterChange={(filtersValues) => onFilter(filtersValues)}
        filtersComponent={filtersComponent}
        handleSubmit={productsListSubmit}
        isLoading={isFetching}
        take="20"
        setPage={(data) => setPage({ ...data, filters })}
        pagination={pagination}
        error={errorsList || errorList}
        clickable
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <>
      <CalculateForm
        control={control}
        submitInProgress={submitInProgress}
        calculate={calculate}
        onSubmit={onCalculateSubmit}
        isEditable={Boolean(
          activePermissions?.includes(PermissionEnum.SETTINGS_FACTORS_ACTION) &&
          currentFactor,
        )}
      />
      <div className={cn.productRiskBottom}>
        <div className={cn.productRiskControls}>
          {activePermissions?.includes(
            PermissionEnum.SETTINGS_PRODUCTS_CREATE_ACTION,
          ) && (
            <NavLink
              to={
                clientType === RiskScoreClientTypeEnum.INDIVIDUAL
                  ? ROUTES.INDIVIDUAL_CLIENTS_RISK_PRODUCTS_CREATE
                  : ROUTES.CORPORATE_CLIENTS_RISK_PRODUCTS_CREATE
              }
              className={cn.productRiskLink}
            >
              <IconExpand />
              Add Product
            </NavLink>
          )}
        </div>
        <div className={cn.productRiskTableWrapper} ref={scrollRef}>
          {content}
        </div>
        <Outlet />
      </div>
    </>
  );
};

export default ClientsProductsList;
